import z from 'zod'
import { pgTable, serial, integer, text, timestamp, jsonb } from 'drizzle-orm/pg-core'
import { createSelectSchema } from 'drizzle-zod'
import type { AiLabelStyle } from '../../platforms/aiDisclosure'
import { pgUser } from './user'

export const pgBrand = pgTable('brand', {
  id: serial('id').primaryKey(),
  userId: integer('user_id')
    .notNull()
    .references(() => pgUser.id, { onDelete: 'cascade' }),

  name: text('name').notNull(),
  // IANA timezone identifier (e.g. 'Europe/Berlin'). Drives when automation
  // reminders fire — see backend/src/routes/eventBridge/generateReminders.ts.
  timezone: text('timezone').notNull().default('UTC'),
  // Remembers the home page's moodboard pick across sessions/devices. No FK on
  // purpose (moodboard.ts imports this file); a dangling id is handled by the
  // home page falling back to the first ready board.
  lastUsedMoodboardId: integer('last_used_moodboard_id'),

  // Default language CASSI generates post content in (free-form, e.g. 'English',
  // 'Deutsch'). Nullable: when null, generation falls back to inferring the
  // language from the topic (legacy behavior). A moodboard.language override
  // takes precedence over this. Set during onboarding.
  language: text('language'),

  // When the brand finished onboarding. Null = onboarding not complete → the
  // app routes the user into the onboarding flow. Replaces the old implicit
  // "a persona exists" gate. The migration backfills existing brands so they
  // are not thrown back into onboarding.
  onboardedAt: timestamp('onboarded_at'),

  // The brand's saved appearance for the baked AI label, used as the starting
  // point for new posts. Nullable on purpose: null means "use CASSI's tuned
  // defaults", which is also what "Reset to defaults" restores, so there is
  // nothing to backfill and the two concepts stay distinguishable. Brand rather
  // than user because a label's look is brand identity, and it follows the same
  // pattern as `lastUsedMoodboardId` — a remembered preference, not content.
  aiLabelStyle: jsonb('ai_label_style').$type<AiLabelStyle>(),

  createdAt: timestamp('created_at').notNull().defaultNow(),
  updatedAt: timestamp('updated_at').notNull().defaultNow(),
})

export const zBrand = createSelectSchema(pgBrand).extend({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type Brand = z.infer<typeof zBrand>
